import React, { useRef, useState, useEffect } from 'react';
import Spacer from '../Spacer';
import { calcOffsetRatio, updatePos } from './scrollCommon';
import Grabber from '../svg/ScrollbarGrabber';
import PropTypes from 'prop-types';

const ScrollbarSvg = ({
    id = 'scrollbar-svg',
    disabled,
    onScroll,
    gutterOffset,
    scrollableLength,
    maxVisibleLength,
    scrollPx = 0,
    isHorizontal = false,
    isDynamic = false,
    onMouseDown,
    onMouseUp,
    onMouseMove,
    allowPropagation = false,
    dynamicScalingFactor = 0.2,
    onSizeChange,
    ...props
}) => {
    disabled = disabled ?? scrollableLength <= maxVisibleLength;

    const scrollbarRef = useRef(null);
    const [scrollbarLength, setScrollbarLength] = useState(0);

    useEffect(() => {
        setScrollbarLength(scrollbarRef.current?.scrollHeight || 63);
        if (typeof onSizeChange === 'function') {
            onSizeChange(scrollbarRef.current?.scrollHeight);
        }
    }, [scrollbarRef.current]);

    if (maxVisibleLength < 1) maxVisibleLength = 1;

    const offsetRatio = calcOffsetRatio(
        scrollableLength,
        maxVisibleLength,
        scrollbarLength
    );

    const mouseDown = (e) => {
        if (disabled) return;
        if (!allowPropagation) e.stopPropagation();
        document.addEventListener('mouseup', mouseUp, true);
        document.addEventListener('mousemove', mouseMove, true);
        document.body.style['pointer-events'] = 'none';
        e.preventDefault();
        if (typeof onMouseDown === 'function') onMouseDown(e);
    };

    const mouseMove = (event) => {
        if (disabled) return;
        if (!allowPropagation) event.stopPropagation();
        const maxPx = gutterOffset + maxVisibleLength - scrollbarLength;

        const newPos =
            event[isHorizontal ? 'pageX' : 'pageY'] - scrollbarLength / 2;

        if (newPos <= gutterOffset) {
            onScroll(updatePos(0, offsetRatio));
        } else if (newPos > maxPx) {
            onScroll(updatePos(maxPx - gutterOffset, offsetRatio));
        } else {
            onScroll(updatePos(newPos - gutterOffset, offsetRatio));
        }
        if (typeof onMouseMove === 'function') onMouseMove(event);
    };

    const mouseUp = (event) => {
        if (!allowPropagation) event.stopPropagation();
        document.removeEventListener('mousemove', mouseMove, true);
        document.removeEventListener('mouseup', mouseUp, true);
        document.body.style['pointer-events'] = 'auto';
        if (typeof onMouseUp === 'function') onMouseUp(event);
    };

    if (scrollbarLength > 0 && scrollbarLength > scrollableLength) {
        return isHorizontal ? <Spacer height={16} /> : <Spacer width={16} />;
    }

    const rotationOffset = isDynamic ? -23.5 / offsetRatio : -23.5;
    let desiredGrabberLength = 63;

    if (isDynamic) {
        desiredGrabberLength = 63 / offsetRatio;
        // Prevent the grabber from getting too small, or too big
        if (desiredGrabberLength < 14) {
            desiredGrabberLength = 14;
        } else if (desiredGrabberLength > maxVisibleLength * 0.9) {
            desiredGrabberLength = maxVisibleLength * 0.9;
        }
    }

    return (
        <Grabber
            {...props}
            length={desiredGrabberLength}
            ref={scrollbarRef}
            style={{
                position: 'relative',
                boxSizing: 'border-box',
                transform: isHorizontal
                    ? `rotate(90deg) translate(${rotationOffset}px,${rotationOffset}px)`
                    : undefined,
                ...(disabled
                    ? {
                          opacity: 0,
                          [isHorizontal ? 'left' : 'top']: scrollPx
                      }
                    : {
                          ...(isHorizontal
                              ? {
                                    left:
                                        scrollPx > 0
                                            ? scrollPx / offsetRatio
                                            : 0
                                }
                              : {
                                    top:
                                        scrollPx > 0
                                            ? scrollPx / offsetRatio
                                            : 0
                                }),
                          cursor: 'grab'
                      })
            }}
            onMouseDown={mouseDown}
        />
    );
};

ScrollbarSvg.defaultProps = {
    id: 'scrollbar-svg',
    allowPropagation: false,
    scrollPx: 0,
    isHorizontal: false,
    isDynamic: false,
    dynamicScalingFactor: 0.2
};

ScrollbarSvg.propTypes = {
    id: PropTypes.string,
    allowPropagation: PropTypes.bool,
    disabled: PropTypes.bool,
    onScroll: PropTypes.func,
    gutterOffset: PropTypes.number,
    scrollableLength: PropTypes.number,
    maxVisibleLength: PropTypes.number,
    scrollPx: PropTypes.number,
    isHorizontal: PropTypes.bool,
    isDynamic: PropTypes.bool,
    onMouseDown: PropTypes.func,
    onMouseUp: PropTypes.func,
    onMouseMove: PropTypes.func,
    onSizeChange: PropTypes.func
};

export default ScrollbarSvg;
